<script setup lang="ts">
/**
 * Banner «Novità» in alto nel layout: UNA card alla volta, non invasiva, chiudibile con la «×» (il server ricorda la chiusura
 * per questo account). Non compare: con l'interruttore generale spento (l'elenco è vuoto), durante il tour guidato, con il
 * cambio password obbligatorio, in una sessione passpartout, né sulla pagina a cui il suo pulsante rimanda.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useNovitaStore } from '../stores/novita'
import { percorsoInterno } from '../lib/testiNovita'
import NovitaCard from './NovitaCard.vue'

const auth = useAuthStore()
const store = useNovitaStore()
const { corrente } = storeToRefs(store)
const route = useRoute()
const router = useRouter()

const utenteId = computed(() => auth.me?.userId ?? null)

// Il tour guidato mette `tour-guide-active` sul body mentre è in corso: in quel momento il banner si toglie di mezzo.
const tourInCorso = ref(false)
let osservatore: MutationObserver | null = null
function leggiTour(): void {
  tourInCorso.value = document.body.classList.contains('tour-guide-active')
}
onMounted(() => {
  leggiTour()
  osservatore = new MutationObserver(leggiTour)
  osservatore.observe(document.body, { attributes: true, attributeFilter: ['class'] })
})
onBeforeUnmount(() => osservatore?.disconnect())

watch(utenteId, (id) => {
  if (id != null && !auth.me?.passpartout) void store.carica(id)
  else store.azzera()
}, { immediate: true })

const visibile = computed(() => {
  const a = corrente.value
  if (!a) return false
  if (tourInCorso.value) return false
  if (auth.me?.mustChangePassword === true || auth.me?.passpartout === true) return false
  // Se il pulsante porta alla pagina in cui l'utente si trova già, il banner non serve.
  const link = percorsoInterno(a.link)
  if (link && route.path === link.split(/[?#]/)[0]) return false
  return true
})

// Statistiche: «visto» la prima volta che il banner è davvero sotto gli occhi dell'utente (non durante il tour, ecc.).
watch([visibile, corrente], ([v, a]) => {
  if (v && a) store.segnala(a.id, 'visto')
}, { immediate: true })

async function apri(): Promise<void> {
  const a = corrente.value
  const link = percorsoInterno(a?.link)
  if (!a || !link) return
  store.segnala(a.id, 'cliccato')
  void store.chiudi(a.id)        // l'ha visto: non glielo riproponiamo
  await router.push(link)
}
</script>

<template>
  <div
    v-if="visibile && corrente"
    class="novita-banner"
    aria-live="polite"
  >
    <NovitaCard
      :annuncio="corrente"
      chiudibile
      @apri="apri"
      @chiudi="store.chiudi(corrente.id)"
    />
  </div>
</template>

<style scoped>
.novita-banner { margin: 0 0 1rem; }
@media (prefers-reduced-motion: no-preference) {
  .novita-banner { animation: novita-entra 0.25s ease-out; }
}
@keyframes novita-entra { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
</style>
